import React, { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { Formik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import html2canvas from "html2canvas";
import { selectUser } from "redux/slices/userSlice";
import { setShowMessage } from "redux/slices/uiSlice";
import { Select, Textarea } from "components/Shared/mui-formik-inputs";
import { coreApi } from "api/core";
import { getConsoleLogBlob, getNetworkLogBlob } from "diagnostics";

const FEEDBACK_TYPES = ["Question", "Improvement", "Error", "Bug"];
const FEEDBACK_BUTTON_ATTR = "data-feedback-trigger";
const SHOW_IN_APP_FEEDBACK = false;

const useStyles = makeStyles((theme) => ({
  supportButton: {
    position: "fixed",
    top: "50%",
    right: 0,
    transform: "translateY(-50%) rotate(180deg)",
    zIndex: 1500,
    backgroundColor: "#6F9CEB",
    color: "white",
    border: "none",
    cursor: "pointer",
    padding: "16px 8px",
    borderTopRightRadius: 6,
    borderBottomRightRadius: 6,
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    writingMode: "vertical-rl",
    textOrientation: "mixed",
    fontSize: 14,
    fontWeight: 600,
    letterSpacing: 0.5,
    boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
    transition: "padding 0.15s ease, background-color 0.15s ease",
    "&:hover": {
      backgroundColor: "#5a80c7",
      padding: "22px 11px",
    },
    "&:disabled": {
      cursor: "wait",
      opacity: 0.85,
    },
  },
  dialog: {
    zIndex: 1600,
    "& .MuiDialog-paper": {
      backgroundColor: "#FFFFFF",
      color: "#121212",
      borderRadius: 8,
      maxWidth: 560,
      width: "100%",
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(2.5, 3, 0),
  },
  title: {
    fontWeight: 700,
    fontSize: 20,
    lineHeight: "28px",
    color: "#333333",
  },
  meta: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: "#F5F5F5",
    borderRadius: 4,
  },
  metaRow: {
    fontSize: 13,
    color: "#555",
    marginBottom: 4,
  },
  screenshotPreview: {
    height: 48,
    width: "auto",
    maxWidth: "100%",
    objectFit: "contain",
    borderRadius: 4,
    border: "1px solid #E0E0E0",
    backgroundColor: "#FAFAFA",
    display: "block",
    cursor: "pointer",
  },
}));

const captureFullPageScreenshot = async () => {
  const canvas = await html2canvas(document.documentElement, {
    useCORS: true,
    allowTaint: true,
    logging: false,
    scrollX: 0,
    scrollY: -window.scrollY,
    windowWidth: document.documentElement.scrollWidth,
    windowHeight: document.documentElement.scrollHeight,
    scale: Math.min(1, 1920 / Math.max(document.documentElement.scrollWidth, 1)),
    ignoreElements: (element) => element?.hasAttribute?.(FEEDBACK_BUTTON_ATTR),
  });

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Failed to create screenshot"));
          return;
        }
        resolve(blob);
      },
      "image/png",
      0.92,
    );
  });
};

const FeedbackButton = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const showFeedbackButton = SHOW_IN_APP_FEEDBACK;
  const screenshotRef = useRef(null);
  const previewUrlRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        type: Yup.string().oneOf(FEEDBACK_TYPES).required(t("Required")),
        message: Yup.string().trim().required(t("Required")),
      }),
    [t],
  );

  const typeOptions = useMemo(
    () =>
      FEEDBACK_TYPES.map((type) => ({
        value: type,
        label: t(`Feedback type ${type}`),
      })),
    [t],
  );

  const userDisplayName = [user?.name, user?.surname].filter(Boolean).join(" ") || "-";
  const supplierLocationName = user?.SupplierLocation?.name || "-";

  const resetScreenshot = () => {
    screenshotRef.current = null;
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewUrl(null);
  };

  const handleMailtoSupport = () => {
    const currentUrl = window.location.href;
    const userEmail = user?.email || "";
    const accountName = user?.Supplier?.name || "";
    const emailSubject = encodeURIComponent(
      t("Support Request regarding Marie WebApp"),
    );
    const emailBody = `${t("Please describe your issue here")}:





_____________________________________________________________

${t("Please do not Edit the following information")}:

${t("User")}: ${userEmail}

${t("Account")}: ${accountName}

URL: ${currentUrl}`;

    const encodedBody = encodeURIComponent(emailBody);
    window.location.href = `mailto:support@marie-app.de?subject=${emailSubject}&body=${encodedBody}`;
  };

  const handleOpen = async () => {
    if (!showFeedbackButton) {
      handleMailtoSupport();
      return;
    }

    if (capturing || submitting) {
      return;
    }

    setCapturing(true);
    try {
      await new Promise((resolve) => requestAnimationFrame(() => resolve()));
      const blob = await captureFullPageScreenshot();
      screenshotRef.current = blob;
      const url = URL.createObjectURL(blob);
      previewUrlRef.current = url;
      setPreviewUrl(url);
      setOpen(true);
    } catch (error) {
      resetScreenshot();
      dispatch(
        setShowMessage({
          description: t("Failed to capture screenshot"),
          type: "error",
        }),
      );
    } finally {
      setCapturing(false);
    }
  };

  const handleClose = () => {
    if (submitting) {
      return;
    }
    setOpen(false);
    resetScreenshot();
  };

  const handleSubmit = async (values, { resetForm }) => {
    if (!screenshotRef.current) {
      dispatch(
        setShowMessage({
          description: t("Failed to submit feedback"),
          type: "error",
        }),
      );
      return;
    }

    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("type", values.type);
      formData.append("message", values.message.trim());
      formData.append("page_url", window.location.href);
      formData.append("screenshot", screenshotRef.current, "screenshot.png");
      formData.append("consoleLog", getConsoleLogBlob(), "console.log");
      formData.append("networkLog", getNetworkLogBlob(), "network.json");

      const result = await coreApi.post("/feedback", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      resetForm();
      setOpen(false);
      resetScreenshot();

      if (result?.email_sent === false) {
        dispatch(
          setShowMessage({
            description: t("Feedback saved, but email could not be sent."),
            type: "warning",
          }),
        );
      } else {
        dispatch(
          setShowMessage({
            description: t("Feedback submitted successfully"),
            type: "success",
          }),
        );
      }
    } catch (error) {
      dispatch(
        setShowMessage({
          description: t("Failed to submit feedback"),
          type: "error",
        }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!showFeedbackButton) {
    return createPortal(
      <button
        type="button"
        aria-label="support"
        className={classes.supportButton}
        onClick={handleMailtoSupport}
        {...{ [FEEDBACK_BUTTON_ATTR]: true }}
      >
        {t("Feedback")}
      </button>,
      document.body,
    );
  }

  return createPortal(
    <>
      <button
        type="button"
        aria-label="feedback"
        className={classes.supportButton}
        onClick={handleOpen}
        disabled={capturing || submitting || open}
        {...{ [FEEDBACK_BUTTON_ATTR]: true }}
      >
        {capturing ? t("Loading") : t("Feedback")}
      </button>

      <Dialog
        open={open}
        onClose={handleClose}
        className={classes.dialog}
        style={{ zIndex: 1600 }}
        fullWidth
        maxWidth="sm"
        disableEnforceFocus
        disableAutoFocus
      >
        <Box className={classes.header}>
          <Typography className={classes.title}>{t("Send Feedback")}</Typography>
          <IconButton
            size="small"
            onClick={handleClose}
            disabled={submitting}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Formik
          initialValues={{ type: "Question", message: "" }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, errors, handleChange, handleBlur, handleSubmit: submitForm }) => (
            <form onSubmit={submitForm}>
              <DialogContent>
                <Box className={classes.meta}>
                  <Typography className={classes.metaRow}>
                    {t("User")}: {userDisplayName} (ID: {user?.id ?? "-"})
                  </Typography>
                  <Typography className={classes.metaRow}>
                    {t("Email")}: {user?.email || "-"}
                  </Typography>
                  <Typography className={classes.metaRow}>
                    {t("Supplier Location")}: {supplierLocationName}
                  </Typography>
                </Box>

                <Select
                  name="type"
                  label={t("Feedback Type")}
                  value={values.type}
                  options={typeOptions}
                  errors={errors}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  MenuProps={{
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left",
                    },
                    getContentAnchorEl: null,
                    style: { zIndex: 1700 },
                  }}
                />

                <Textarea
                  name="message"
                  label={t("Message")}
                  value={values.message}
                  errors={errors}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  rows={5}
                />

                {previewUrl && (
                  <Box mt={1}>
                    <img
                      src={previewUrl}
                      alt={t("Screenshot")}
                      className={classes.screenshotPreview}
                      role="button"
                      tabIndex={0}
                      onClick={() => window.open(previewUrl, "_blank", "noopener,noreferrer")}
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          window.open(previewUrl, "_blank", "noopener,noreferrer");
                        }
                      }}
                    />
                  </Box>
                )}
              </DialogContent>

              <DialogActions style={{ padding: "8px 24px 24px" }}>
                <Button onClick={handleClose} disabled={submitting}>
                  {t("Cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={submitting}
                  startIcon={submitting ? <CircularProgress size={16} /> : null}
                >
                  {t("Submit")}
                </Button>
              </DialogActions>
            </form>
          )}
        </Formik>
      </Dialog>
    </>,
    document.body,
  );
};

export default FeedbackButton;
